import { apiCall } from '@/lib/api-client';

export type UploaderQueueStatus = 'pending' | 'flagged' | 'uploaded' | 'all';
export type UploaderQueueSort = 'oldest' | 'newest';
export type UploaderQueueClaimed = 'visible' | 'mine' | 'unclaimed';

export interface UploaderFilterPerson {
  id: string;
  name: string;
}

export interface UploaderFilterOptions {
  agents: UploaderFilterPerson[];
  uploaders: UploaderFilterPerson[];
}

export interface UploaderQueueFilters {
  limit?: number;
  offset?: number;
  sort?: UploaderQueueSort;
  agentId?: string;
  uploadedById?: string;
  dateFrom?: string;
  dateTo?: string;
  sla24h?: boolean;
  claimed?: UploaderQueueClaimed;
  q?: string;
}

/**
 * Cluster H v2 SUB-F.2 (LD-H2-14/15) — structured flag-error reasons.
 * 'no_medicaid' is the special subtype that sets no_medicaid_flagged_at
 * and skips agent bonus (LD-H2-15). DB CHECK constraint pins these
 * (migration 028).
 */
export type FlagErrorReason =
  | 'no_medicaid'
  | 'data_inconsistent'
  | 'cin_invalid'
  | 'duplicate'
  | 'other';

export interface FlagErrorBody {
  reason: FlagErrorReason;
  reasonText?: string | null;
}

export interface UploaderQueueRow {
  id: string;
  clientId: string;
  clientLastName: string;
  clientCinLast6: string;
  agentId: string;
  agentName: string;
  submittedAt: string;
  hoursSinceSubmit: number;
  positiveDomains: string[];
  positiveCount: number;
  findhelpUploadedAt: string | null;
  findhelpId: string | null;
  findhelpUploadNotes: string | null;
  findhelpFlaggedError: string | null;
  findhelpFlaggedAt: string | null;
  findhelpUploadedByName: string | null;
  // Cluster H v2 LD-H2-11 — claim-on-action (SUB-B/F)
  claimUploaderId: string | null;
  claimedAt: string | null;
  claimedByName: string | null;
  // Cluster H v2 SUB-F.3 — sub-screening linkage
  isSubScreening?: boolean;
  parentClientLastName?: string | null;
  parentClientCinLast6?: string | null;
  parentClientId?: string | null;
}

export interface UploaderQueueResponse {
  items: UploaderQueueRow[];
  total: number;
  pending: number;
  pending24hPlus: number;
  flagged: number;
  uploaded: number;
}

export interface UploaderScreeningDetail {
  id: string;
  agentId: string;
  agentName: string | null;
  /** F5-T4: URL base de FindHelp para el paso 1; null = boton inerte. */
  findhelpBaseUrl?: string | null;
  /** F-1/U-b: flag sin-Medicaid y su cierre admin (vista admin). */
  noMedicaidFlaggedAt?: string | null;
  ineligibilityClosedAt?: string | null;
  startedAt: string;
  submittedAt: string | null;
  completedAt: string | null;
  totalDurationSec: number | null;
  languageUsed: string;
  timeCategory: string | null;
  nextStepNavigator: boolean | null;
  safetyTotalScore: number | null;
  safetyConcernFlag: boolean | null;
  positiveDomains: string[];
  client: {
    id: string;
    cin: string;
    firstName: string;
    lastName: string;
    dob: string;
    addressLine1: string | null;
    city: string | null;
    postalCode: string | null;
    phoneHome: string | null;
    phoneCell: string | null;
    preferredLanguage: string | null;
  };
  answers: Array<{
    domain: string;
    answer: Record<string, unknown>;
    notes: string | null;
    positiveFinding: boolean;
    answeredAt: string;
  }>;
  findhelp: {
    uploadedAt: string | null;
    uploadedById: string | null;
    uploadedByName: string | null;
    findhelpId: string | null;
    notes: string | null;
    flaggedError: string | null;
    flaggedAt: string | null;
    noMedicaid: boolean;
  };
  /** v0.6.0 Fase 11.13 — household linkage when this client was created
   *  via household_promotion. null otherwise. CIN masked to last-6 for
   *  uploader audience. */
  promotedFrom: {
    householdMemberId: string;
    sourceScreeningId: string;
    promotedAt: string | null;
    promotedByName: string | null;
    promotedByEmail: string | null;
    parentClientId: string | null;
    parentClientFirstName: string | null;
    parentClientLastName: string | null;
    parentClientCinLast6: string | null;
  } | null;
  /** v0.6.1 Fase 9.2 — most-recent EDIT_CLIENT audit row (or null). */
  clientLastEdit: {
    editedAt: string;
    editorId: string;
    editorFullName: string | null;
    editorRole: string;
    fieldsChanged: string[];
    reason: string | null;
  } | null;
}

export interface UploaderStats {
  today: { uploaded: number; pending: number; oldestPendingHours: number; avgTimeToUploadHours: number };
  week:  { uploaded: number };
  month: { uploaded: number };
  total: { uploaded: number };
  flagged: number;
  /** v0.6.1 Fase 19 — targets + tier ladder + SLA hours from system_config. */
  targets: {
    dailyTarget: number;
    weeklyTarget: number;
    monthlyTarget: number;
    currentTier: { name: 't3' | 't2' | 't1' | 'base' | 'below_floor'; threshold: number };
    nextTier: { name: 't3' | 't2' | 't1' | 'base'; threshold: number } | null;
    slaOldestPendingHours: number;
    slaAvgTimeToUploadHours: number;
  };
}

export interface UploaderTeamStats {
  totals: { pending: number; flagged: number; uploadedToday: number; pending24hPlus: number; noMedicaidToday: number; noMedicaidWeek: number; noMedicaidMonth: number; noMedicaidTotal: number; processedTotal: number; noMedicaidPercent: number };
  perUploader: Array<{ id: string; fullName: string; todayUploaded: number; weekUploaded: number; pending: number; oldestPendingHours: number }>;
  overdueScreenings: Array<{ id: string; clientLastName: string; clientCinLast6: string; agentName: string; submittedAt: string; hoursSinceSubmit: number }>;
}

export interface UploaderDetails {
  user: {
    id: string;
    fullName: string;
    email: string;
    role: string;
    active: boolean;
    lastLoginAt: string | null;
  };
  stats: UploaderStats;
  recentWork: Array<{
    id: string;
    clientLastName: string;
    clientCinLast6: string;
    submittedAt: string | null;
    processedAt: string;
    timeToProcessHours: number | null;
    status: 'uploaded' | 'flagged' | 'no_medicaid';
    findhelpId: string | null;
    notes: string | null;
    flaggedError: string | null;
    agentName: string | null;
  }>;
  pendingQueueCount: number;
  longestPendingHours: number;
  teamAvgTimeToUploadHours: number;
  hourlyUploadsToday: Array<{ hour: number; count: number }>;
  audit: Array<{
    id: string;
    action: string;
    occurredAt: string;
    entityType: string | null;
    entityId: string | null;
    metadata: Record<string, unknown> | null;
  }>;
}

export const uploaderApi = {
  getQueue: (status: UploaderQueueStatus, opts: UploaderQueueFilters = {}) => {
    const p = new URLSearchParams();
    p.set('status', status);
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.offset) p.set('offset', String(opts.offset));
    if (opts.sort) p.set('sort', opts.sort);
    if (opts.agentId) p.set('agentId', opts.agentId);
    if (opts.uploadedById) p.set('uploadedById', opts.uploadedById);
    if (opts.dateFrom) p.set('dateFrom', opts.dateFrom);
    if (opts.dateTo) p.set('dateTo', opts.dateTo);
    if (opts.sla24h) p.set('sla24h', '1');
    if (opts.claimed && opts.claimed !== 'visible') p.set('claimed', opts.claimed);
    if (opts.q) p.set('q', opts.q);
    return apiCall<UploaderQueueResponse>(`/uploader/queue?${p.toString()}`);
  },

  getFilterOptions: () => apiCall<UploaderFilterOptions>('/uploader/filter-options'),

  getDetail: (id: string) =>
    apiCall<UploaderScreeningDetail>(`/uploader/screenings/${id}`),

  // F5-T4 (Uploader 02 + F-1): uploadKind declara como quedo cargado;
  // noMedicaid quedo retirado (el server lo rechaza) y el ID no se pega.
  markUploaded: (id: string, body: { uploadKind?: 'complete' | 'adapted' | 'duplicate'; adaptedDomain?: string; findhelpId?: string; notes?: string; noMedicaid?: boolean }) =>
    apiCall<{ ok: true; id: string }>(`/uploader/screenings/${id}/mark-uploaded`, {
      method: 'POST',
      body,
    }),

  // F5-T4/U-b (F-1): cierre por inelegibilidad — SOLO admin, razon obligatoria.
  closeIneligible: (id: string, body: { reason: string }) =>
    apiCall<{ ok: true; id: string }>(`/admin/uploader/screenings/${id}/close-ineligible`, {
      method: 'POST',
      body,
    }),

  flagError: (id: string, body: FlagErrorBody) =>
    apiCall<{ ok: true; id: string }>(`/uploader/screenings/${id}/flag-error`, {
      method: 'POST',
      body,
    }),

  // Cluster H v2 LD-H2-11 — claim-on-action (SUB-B/F.1)
  claim: (screeningId: string) =>
    apiCall<{ ok: true; screeningId: string; claimedAt: string }>(
      `/uploader/queue/${screeningId}/claim`,
      { method: 'POST' },
    ),
  release: (screeningId: string) =>
    apiCall<{ ok: true; screeningId: string }>(
      `/uploader/queue/${screeningId}/release`,
      { method: 'POST' },
    ),

  getMyStats: () => apiCall<UploaderStats>('/uploader/me/stats'),

  // Admin team-stats
  getAdminTeamStats: () => apiCall<UploaderTeamStats>('/admin/uploader/team-stats'),

  // Admin can also drill into screening detail (read-only — UI hides the action buttons)
  getAdminDetail: (id: string) => apiCall<UploaderScreeningDetail>(`/admin/uploader/screenings/${id}`),

  // Agent reads their OWN screening detail — same payload shape, but the API
  // enforces screening.agent_id === user.sub. Used by the compact 'My
  // submissions today' panel on the QueuePage.
  getOwnAgentDetail: (id: string) => apiCall<UploaderScreeningDetail>(`/agents/me/screenings/${id}`),

  // Admin drill-down: full details for a single uploader (stats, recent work,
  // audit slice). Backed by /admin/uploader/uploaders/:id/details.
  getAdminUploaderDetails: (id: string) =>
    apiCall<UploaderDetails>(`/admin/uploader/uploaders/${id}/details`),
};
